import type { PromoHit, SuggestionHit, TopicHit, VariantHit } from './algolia'
import {
  directSearchClient,
  mainIndex,
  promotionsIndex,
  querySuggestionsIndex,
  seasonalTrendsIndex,
} from './algoliaSearchConfig'

// Primi N topic in empty state (per Wave 0 usiamo 2).
export const EMPTY_STATE_TOPIC_HITS = 2

// TBD: valori definitivi
export const TYPING_QS_HITS = 3
export const TYPING_CATEGORY_MAX = 2
export const TYPING_BRAND_MAX = 3
export const TYPING_PRODUCT_HITS = 4
export const ALGOLIA_COOKIE_DURATION = 15552000000 // 180 days in ms

export type EmptyStateResult = {
  promoHit: PromoHit | null
  topicHits: TopicHit[]
}

export type FacetValue = {
  value: string
  count: number
}

export type TypingStateResult = {
  query: string
  products: VariantHit[]
  productNbHits: number
  productQueryID: string
  suggestions: Array<SuggestionHit>
  categories: FacetValue[]
  brands: FacetValue[]
}

type SearchRequest = {
  indexName: string
  query?: string
  hitsPerPage?: number
  facets?: string[]
  maxValuesPerFacet?: number
}

const emptyStateFallback: EmptyStateResult = {
  promoHit: null,
  topicHits: [],
}

const emptyTyping = (query: string): TypingStateResult => ({
  query,
  products: [],
  productNbHits: 0,
  productQueryID: '',
  suggestions: [],
  categories: [],
  brands: [],
})

const readHits = <T>(result: unknown): T[] => {
  if (result && typeof result === 'object' && 'hits' in result) {
    const fixedResult = (result as { hits: T[]; queryID: string }).hits.map(
      (hit) => {
        return {
          ...hit,
          __queryID: 'queryID' in result ? result.queryID : '',
        }
      }
    )
    return (fixedResult ?? []) as T[]
  }

  return []
}

const readNbHits = (result: unknown): number => {
  if (result && typeof result === 'object' && 'nbHits' in result) {
    const value = (result as { nbHits: number }).nbHits

    return typeof value === 'number' ? value : 0
  }

  return 0
}

const readQueryID = (result: unknown): string => {
  if (result && typeof result === 'object' && 'queryID' in result) {
    const value = (result as { queryID: string }).queryID

    return typeof value === 'string' ? value : ''
  }

  return ''
}

type FacetCounts = Record<string, number>

const readFacetValues = (result: unknown, attribute: string): FacetValue[] => {
  if (!result || typeof result !== 'object' || !('facets' in result)) {
    return []
  }

  const facetsByAttribute = (result as { facets?: Record<string, FacetCounts> })
    .facets
  const countsForAttribute: FacetCounts = facetsByAttribute?.[attribute] ?? {}

  return Object.entries(countsForAttribute)
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count)
}

// Empty state (query ≤2): una multiquery: promo(1) + topic(N).
export async function fetchEmptyStateMultiquery(
  userToken: string | undefined
): Promise<EmptyStateResult> {
  const requests: SearchRequest[] = [
    {
      indexName: promotionsIndex,
      query: '',
      hitsPerPage: 1,
      userToken,
      clickAnalytics: true,
    },
    {
      indexName: seasonalTrendsIndex,
      query: '',
      hitsPerPage: EMPTY_STATE_TOPIC_HITS,
      userToken,
      clickAnalytics: true,
    },
  ].filter(Boolean) as SearchRequest[]

  if (requests.length === 0) return emptyStateFallback

  try {
    const response = await directSearchClient.search({ requests })
    const results = response.results ?? []

    let resultIndex = 0

    const hits = readHits<PromoHit>(results[resultIndex++])
    const promoHit: PromoHit | null = hits[0] ?? null

    const topicHits: TopicHit[] = readHits<TopicHit>(
      results[resultIndex++]
    ).slice(0, EMPTY_STATE_TOPIC_HITS)

    return { promoHit, topicHits }
  } catch {
    return emptyStateFallback
  }
}

// Digitazione (≥3 char): multiquery da 4
export async function fetchTypingMultiquery(
  rawQuery: string,
  userToken: string | undefined
): Promise<TypingStateResult> {
  const query = rawQuery.trim()

  if (!query || !mainIndex) return emptyTyping(query)

  const requests: SearchRequest[] = [
    {
      indexName: mainIndex,
      query,
      hitsPerPage: TYPING_PRODUCT_HITS,
      clickAnalytics: true,
      userToken,
    },
    {
      indexName: querySuggestionsIndex,
      query,
      hitsPerPage: TYPING_QS_HITS,
      clickAnalytics: true,
      userToken,
    },
    {
      indexName: mainIndex,
      query,
      hitsPerPage: 0,
      facets: ['categoryPath'],
      maxValuesPerFacet: TYPING_CATEGORY_MAX,
      userToken,
    },
    {
      indexName: mainIndex,
      query,
      hitsPerPage: 0,
      facets: ['brand'],
      maxValuesPerFacet: TYPING_BRAND_MAX,
      userToken,
    },
  ].filter(Boolean) as SearchRequest[]

  try {
    const response = await directSearchClient.search({ requests })
    const results = response.results ?? []

    let i = 0
    const productResult = results[i++]
    const products = readHits<VariantHit>(productResult)
    const productNbHits = readNbHits(productResult)
    const productQueryID = readQueryID(productResult)
    const suggestions = readHits<SuggestionHit>(results[i++])
    const categories = readFacetValues(results[i++], 'categoryPath')
    const brands = readFacetValues(results[i++], 'brand')

    return {
      query,
      products,
      productNbHits,
      productQueryID,
      suggestions,
      categories,
      brands,
    }
  } catch {
    return emptyTyping(query)
  }
}
